import type { TemplateField } from './domain/fieldMatcher'
import type { AppThunk } from '../../app/store'
import fetchHelper from '../../utils/fetchHelper'

declare const __API_SERVER_HOST__: string

export interface EditableTableColumn {
  key: string
  title: string
  width?: number
}

export interface EditableTableDefinition {
  variant?: 'standard-three-inspection'
  page: number
  rect: { x: number; y: number; width: number; height: number }
  columns: EditableTableColumn[]
  rows: Array<Record<string, string>>
  maxRows?: number
}

export interface ThreeInspectionTemplate {
  templateID: string
  name: string
  fileURL?: string
  fileHash?: string
  fileSize?: number
  pageCount: number
  fields?: TemplateField[]
  table?: EditableTableDefinition
  createdAt: number
  updatedAt: number
}

interface ApiStatus {
  status: number
  msg?: string
}

interface ApiResponse<T> {
  code: number
  data?: T
  msg?: string
}

function post<T extends ApiStatus>(
  path: string,
  body: object
): AppThunk<Promise<T>> {
  return async (dispatch, getState) => {
    const result = (await fetchHelper(dispatch, getState, {
      url: __API_SERVER_HOST__ + path,
      arguments: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      }
    })) as ApiResponse<T> | null
    const data = result?.data
    // WebXCameraAPI 新旧接口外层成功码并存 0/200，三检业务结果继续使用
    // data.status=0；fetchHelper 负责 401、离线与 Sentry 上报。
    const requestSucceeded = result?.code === 0 || result?.code === 200
    if (!requestSucceeded || !data || data.status !== 0) {
      throw new Error(data?.msg || result?.msg || '操作失败，请稍后重试')
    }
    return data
  }
}

export function saveThreeInspectionTemplate(input: {
  groupID: string
  templateID?: string
  name: string
  fileURL: string
  fileHash?: string
  fileSize?: number
  pageCount: number
  fields: TemplateField[]
  table?: EditableTableDefinition
}): AppThunk<Promise<ThreeInspectionTemplate>> {
  return async (dispatch) => {
    const data = await dispatch(
      post<ApiStatus & { template: ThreeInspectionTemplate }>(
        '/web/workgroup/three-inspection/template/save',
        input
      )
    )
    return data.template
  }
}

export function listThreeInspectionTemplates(
  groupID: string
): AppThunk<Promise<ThreeInspectionTemplate[]>> {
  return async (dispatch) => {
    const data = await dispatch(
      post<ApiStatus & { templates: ThreeInspectionTemplate[] }>(
        '/web/workgroup/three-inspection/template/list',
        { groupID }
      )
    )
    return data.templates ?? []
  }
}

export function getThreeInspectionTemplate(
  groupID: string,
  templateID: string
): AppThunk<Promise<ThreeInspectionTemplate>> {
  return async (dispatch) => {
    const data = await dispatch(
      post<ApiStatus & { template: ThreeInspectionTemplate }>(
        '/web/workgroup/three-inspection/template/detail',
        { groupID, templateID }
      )
    )
    return data.template
  }
}

export function deleteThreeInspectionTemplate(
  groupID: string,
  templateID: string
): AppThunk<Promise<void>> {
  return async (dispatch) => {
    await dispatch(
      post('/web/workgroup/three-inspection/template/delete', {
        groupID,
        templateID
      })
    )
  }
}
